import { useEffect, useMemo, useRef, useState } from 'react';
import { Tooltip } from 'reactstrap';
import { useTranslation } from 'next-i18next';

import { setDelayedDeliveryDetails, updateStepCart } from '@/redux/slices/checkout.slice';
import { setDelayedDeliveryDetailsExpro, updateExproOrder } from '@/redux/slices/expro.slice';
import { CartShippingModel } from '@/models';
import { useAppDispatch, useAppSelector } from '@/redux';
import { dateToStringFormat } from '@/utils/dateUtils';

import CallToActionContainer from '@/components/common/callToAction/CallToActionContainer';
import Form from '@/components/common/form/Form';
import FormField from '@/components/common/form/FormField';
import Icon from '@/components/common/iconedLinks/Icon';
import MillesimaPrice from '@/components/common/price/MillesimaPrice';
import CheckboxV2 from '@/components/common/CheckboxV2';

import styles from './DelayedShipping.module.scss';
import { FIELD_TYPE } from '@/constants/form';
import { PRICE } from '@/constants/price';
import { URLS } from '@/constants/urls';

type DelayedShippingProps = {
  deliveryMode: CartShippingModel;
  isExproCheckout: boolean;
}

export const DelayedShipping = ({ deliveryMode, isExproCheckout = false }: DelayedShippingProps): React.JSX.Element => {
  const dispatch = useAppDispatch();
  const ref = useRef();
  const { t } = useTranslation();
  const dateFormat = useAppSelector(({ root }) => root.storeConf.dateFormat);

  const { expro, checkout } = useAppSelector((state) => state);
  const delayedDeliveryDetails = isExproCheckout ? expro.delayedDeliveryDetails : checkout.delayedDeliveryDetails;
  const exproOrder = expro.exproOrder;

  const [isOpen, setIsOpen] = useState(false);

  const { _id, delayedShipping, delayedDeliveryDates } = deliveryMode;

  const isFree = useMemo(() => delayedShipping.price === 0, [delayedShipping]);

  const [dateMin, dateMax] = useMemo(() => [
    new Date(delayedDeliveryDates[0]),
    new Date(delayedDeliveryDates.at(-1))
  ], [delayedDeliveryDates]);

  const deliveryDateSelected = delayedDeliveryDetails[_id]?.date;

  useEffect(() => {
    updateDeliveryDate(null, false, true);
  }, []);

  const formatLocalDate = (date: Date | null | undefined): string | null => {
    if (!date) return null;

    const year = date.getFullYear();
    const month = `${date.getMonth() + 1}`.padStart(2, '0');
    const day = `${date.getDate()}`.padStart(2, '0');

    return `${year}-${month}-${day}`;
  };

  const handleSubmit = (data) => {
    updateDeliveryDate(formatLocalDate(data.delayedShipping), false, true);
  };

  const handleModify = () => {
    updateDeliveryDate(null, true, true);
  };

  const handleDelete = () => {
    updateDeliveryDate(null, false, true);
  };

  const handleChange = () => {
    updateDeliveryDate(null, !delayedDeliveryDetails[_id]?.isChecked, false);
  };

  const updateDeliveryDate = (date, isChecked, update) => {
    const contentDelayedDeliveryDetails = {
      ...delayedDeliveryDetails,
      [_id]: { date, isChecked }
    };

    if (isExproCheckout) {
      dispatch(setDelayedDeliveryDetailsExpro({
        delayedDeliveryDetails: contentDelayedDeliveryDetails
      }));

      if (update) {
        dispatch(updateExproOrder({
          exproOrder: {
            ...exproOrder,
            shippingMode: {
              ...deliveryMode,
              selectedDeliveryDateDelayed: date
            }
          }
        }));
      }
    } else {
      dispatch(setDelayedDeliveryDetails({
        delayedDeliveryDetails: contentDelayedDeliveryDetails
      }));

      if (update) {
        dispatch(updateStepCart({
          update: {
            shippingMode: {
              ...deliveryMode,
              selectedDeliveryDateDelayed: date
            }
          }
        }));
      }
    }
  };

  return (
    <div className={styles.container} data-test-id="container">
      <Icon
        src={URLS.ICON_DELAYED_SHIPPING}
        alt="hourglass"
        className={styles.icon}
        width={48}
        height={48}
      />
      <div className={styles['header-information']} data-test-id="header-information">
        <span className={styles.title} data-test-id="title">{delayedShipping?.title}</span>
        <Icon alt="more infos" className={styles.infos} src={URLS.ICON_INFO_HELP} id="target" />
        <Tooltip
          target="target"
          isOpen={isOpen}
          toggle={() => setIsOpen(!isOpen)}
        >
          {delayedShipping.longDesc}
        </Tooltip>
      </div>

      <MillesimaPrice
        value={isFree ? PRICE.FREE : +delayedShipping.price}
        className={{ container: `${styles.price} ${isFree ? styles.free : ''}` }}
        isAdditionnalFees
      />

      <p className={styles.description} data-test-id="description">{delayedShipping.desc}</p>

      <div className={styles.footer} data-test-id="footer">
        {!deliveryDateSelected && (
          <div className={styles.checkbox}>
            <CheckboxV2
              onChange={handleChange}
              checked={delayedDeliveryDetails[_id]?.isChecked ? true : false}
              label={delayedShipping.checkBoxDesc}
            />
          </div>
        )}
        {delayedDeliveryDetails[_id]?.isChecked && (
          <div className={styles.form}>
            <Form onSubmit={handleSubmit}>
              <FormField
                id="shippingDate"
                name="delayedShipping"
                label={t('checkout:shipMode.shipFrom')}
                type={FIELD_TYPE.DATE}
                startDate={dateMin}
                dateMin={dateMin}
                dateMax={dateMax}
                includeDates={delayedDeliveryDates?.map((dateString) => new Date(dateString))}
                isRequired
              />
              <CallToActionContainer buttonRef={ref} id="delayedShipping-submit" type="submit">
                {t('btn.validate')}
              </CallToActionContainer>
            </Form>
          </div>
        )}
        {deliveryDateSelected && (
          <div className={styles.resume} data-test-id="resume">
            <div className={styles.date}>
              <Icon src={URLS.CHECKBOX_CONFIRM_CIRCLE_GREEN} alt="confirmed" />
              <span>{t('checkout:shipMode.shippingFrom')}</span>
              <span>{dateToStringFormat(deliveryDateSelected, dateFormat)}</span>
            </div>
            <div className={styles.actions}>
              <span onClick={handleModify}>{t('btn.modify')}</span>
              <Icon src={URLS.EDIT_BLACK} width={12} height={12} alt="modify" />
              <span onClick={handleDelete}>{t('btn.delete')}</span>
              <Icon src={URLS.ORION_CLOSE_BLACK} width={12} height={12} alt="delete" />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default DelayedShipping;
